import { InvoiceCatalogProposal } from '../components/InvoiceCatalogProposal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { draftDomainPath, draftIsExpired, expenseInputSchema, permiteRuta, planRecipeProposal, proposalToImportRows } from '@alexiarest/shared';
import { api } from '../api/client';
import { useDraft, useDrafts, useDiscardDraft, useIngredients, useRecipes, useMe } from '../api/hooks';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary } from '../components/QueryBoundary';
import { TellAlexiaRecipeDrawer } from '../components/TellAlexiaRecipeDrawer';
import { ScanReceiptDrawer } from '../components/ScanReceiptDrawer';
import { useToast } from '../components/Toast';

export function DraftsPage() {
  const drafts = useDrafts();
  const discard = useDiscardDraft();
  const toast = useToast();
  const me = useMe();
  const ingredients = useIngredients();
  const recipes = useRecipes();
  const cache = useQueryClient();
  const [savingAll, setSavingAll] = useState(false);
  const [batchErrors, setBatchErrors] = useState<string[]>([]);
  const ready = (drafts.data ?? []).filter((draft) => {
    const user = me.data?.user;
    if (!user || user.role === 'viewer' || draftIsExpired(draft)) return false;
    if (user.permissions && !permiteRuta(user.permissions, draftDomainPath(draft.kind), 'POST')) return false;
    if (draft.kind === 'expense') return user.role !== 'kitchen' && !draft.payload.requiresReview && !draft.payload.proposal.duplicado && draft.payload.proposal.netAmount > 0 && expenseInputSchema.safeParse(draft.payload.input ?? draft.payload.proposal).success;
    if (draft.kind === 'recipe') return Boolean(ingredients.data && recipes.data && !proposalToImportRows(draft.payload, ingredients.data, recipes.data, Object.fromEntries(draft.payload.lines.flatMap((line, index) => line.ingredientId ? [[index, { ingredientId: line.ingredientId }]] : []))).issues.length);
    if (draft.kind === 'catalog-prices') return draft.payload.lines.some((line) => line.action !== 'ignore') && draft.payload.lines.every((line) => line.action === 'ignore' || (line.action !== 'choose' && (line.unitPrice ?? 0) > 0));
    return true;
  });
  const confirmAll = async () => {
    setSavingAll(true); setBatchErrors([]);
    const errors: string[] = []; let saved = 0;
    for (const draft of ready) {
      try { await api.confirmDraft(draft.id); saved += 1; }
      catch (error) { errors.push(`${draft.summary}: ${error instanceof Error ? error.message : 'No se ha podido guardar.'}`); }
    }
    await cache.invalidateQueries();
    setBatchErrors(errors); setSavingAll(false);
    if (saved) toast.success(`${saved} borradores guardados.`);
  };
  return <>
    <PageHeader title="Pendientes de confirmar" description="Revisa lo que has enviado a Alexia. Cada borrador se guarda cuando lo confirmas." />
    {ready.length > 0 && <div className="card stack"><p>Se guardarán las propuestas listas tal como aparecen. Los duplicados y las dudas necesitan revisión individual.</p><button className="btn" disabled={savingAll} onClick={confirmAll}>{savingAll ? 'Guardando borradores…' : `Confirmar ${ready.length} borradores listos`}</button></div>}
    {batchErrors.length > 0 && <div role="alert" className="card">{batchErrors.map((error, index) => <p key={index}>{error}</p>)}</div>}
    <QueryBoundary query={drafts}>{(items) => <div className="stack">
      {!items.length && <p>No tienes borradores pendientes.</p>}
      {items.map((draft) => <article className="card stack" key={draft.id}>
        <strong>{draft.summary}</strong><span className="muted small">{draft.source === 'app' || draft.source === 'demo' ? 'En la app' : draft.source === 'email' ? 'Por correo' : 'Por WhatsApp'}</span>
        <Link className="btn" to={`/borradores/${draft.id}`}>Revisar y confirmar</Link>
        <button className="btn btn--ghost" onClick={async () => { try { const link = await api.createDraftLink(draft.id); await navigator.clipboard.writeText(link.url); toast.success('Enlace copiado. Caduca en 15 minutos y requiere tu sesión.'); } catch (error) { toast.fail(error); } }}>Copiar enlace</button>
        <button className="btn btn--ghost" disabled={discard.isPending} onClick={() => discard.mutate(draft.id, { onError: (error) => toast.fail(error) })}>Descartar</button>
      </article>)}
    </div>}</QueryBoundary>
  </>;
}

export function DraftReviewPage() {
  const { id = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const redeemed = useRef<string>();
  const [linkError, setLinkError] = useState('');
  const token = search.get('token');
  useEffect(() => {
    if (!token || redeemed.current === token) return;
    redeemed.current = token;
    void api.redeemDraftLink(id, token).catch((error) => setLinkError(error.message)).finally(() => {
      setSearch((current) => { current.delete('token'); return current; }, { replace: true });
    });
  }, [id, token, setSearch]);
  const query = useDraft(id);
  const ingredients = useIngredients();
  const recipes = useRecipes();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const draft = query.data;
  const initialProposal = useMemo(() => draft?.kind === 'recipe' && ingredients.data && recipes.data ? {
    draftId: draft.id, proposal: draft.payload, catalog: ingredients.data, recipeCatalog: recipes.data,
    plan: planRecipeProposal(draft.payload, ingredients.data),
  } : undefined, [draft, ingredients.data, recipes.data]);
  const refresh = () => { void qc.invalidateQueries(); };
  return <>
    {linkError && <p role="alert">{linkError}</p>}
    <PageHeader title="Revisar borrador" description={draft?.summary ?? 'Cargando propuesta…'} />
    <QueryBoundary query={query}>{(item) => item.status !== 'pending' ? <div className="card stack">
      <p>{item.status === 'confirmed' ? 'Este borrador ya está confirmado.' : 'Este borrador ya fue descartado.'}</p>
      {item.result?.recipeId && <Link to={`/recetas/${item.result.recipeId}`}>Abrir receta</Link>}
      {item.result?.expenseId && <Link to="/gastos">Ver gastos</Link>}
      <Link to="/borradores">Volver a pendientes</Link>
    </div> : <>
      {initialProposal && <TellAlexiaRecipeDrawer open initialProposal={initialProposal} onClose={() => navigate('/borradores')} onSaved={(recipeId) => { refresh(); navigate(`/recetas/${recipeId}`); }} />}
      {item.kind === 'expense' && <ScanReceiptDrawer open documentType={item.payload.documentType} initialDraft={{ proposal: item.payload.proposal, key: item.inputKey }} onClose={() => navigate('/borradores')} confirmExpense={async (input, acceptPossibleDuplicate) => {
        const confirmed = await api.confirmDraft(item.id, { expense: input, acceptPossibleDuplicate });
        if (!confirmed.result?.expenseId) throw new Error('No se ha podido confirmar el gasto.');
        refresh();
        return api.getExpense(confirmed.result.expenseId);
      }} />}
      {item.kind === 'answer' && <section className="card stack"><p>Confirmar {item.payload.unitPrice} por {item.payload.unit}.</p><button className="btn" onClick={() => { void api.confirmDraft(item.id).then(refresh).catch((error) => setLinkError(error.message)); }}>Confirmar precio</button><Link to="/ingredientes">Revisar ingrediente</Link></section>}
      {item.kind === 'catalog-prices' && <InvoiceCatalogProposal proposal={item.payload} confirmPrices={async (batch) => {
        await api.confirmDraft(item.id, { prices: batch.items.map((row) => ({ ingredientId: row.id, name: row.input.name, price: row.input.netPrice!, qty: row.input.purchaseQty!, unit: row.input.purchaseUnit! })) });
        refresh();
        return { creados: batch.items.filter((row) => !row.id).length, actualizados: batch.items.filter((row) => row.id).length, errores: [] };
      }} />}
    </>}</QueryBoundary>
  </>;
}
